/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { BUSINESS_RULES } from "@/lib/constants/business/rules";
import type { Database } from "@/lib/supabase/types";
import type { OrganizationSettings } from "@/types/organization-settings";
import { logger } from "@/lib/utils/logger";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { BudgetRates } from "./estimates-types";
import {
  extractBudgetRates,
  orgSettingsToBudgetRates,
} from "./budget-rate-parser";
import {
  deriveBudgetRates,
  type BudgetAssumptionsInput,
} from "../budgets/assumptions";

export {
  extractBudgetRates,
  orgSettingsToBudgetRates,
} from "./budget-rate-parser";
export {
  applyRateOverrides,
  batchFetchCatalogRateOverrides,
  fetchCatalogItemRateOverrides,
  type CatalogRateOverrides,
} from "./budget-catalog-overrides";

/**
 * Per-estimate rate overrides. Stored in calculation_breakdown.overrides.
 * When present, these values replace the corresponding budget rate fields.
 * null = use budget default (clear the override).
 */
export interface EstimateRateOverrides {
  overheadRate?: number | null;
  profitMargin?: number | null;
  contingencyRate?: number | null;
  overheadRecoveryMethod?: BudgetRates["overheadRecoveryMethod"] | null;
  includeContingency?: boolean | null;
  // Method-specific overrides
  overheadFixedAmount?: number | null;
  overheadPerLaborHour?: number | null;
  categoryOverheadRates?: BudgetRates["categoryOverheadRates"] | null;
  materialMarkupRate?: number | null;
  subcontractorMarkupRate?: number | null;
  equipmentMarkupRate?: number | null;
  contingencyBasis?: BudgetRates["contingencyBasis"] | null;
}

function normalizePercentLikeRate(
  value: number | null | undefined,
): number | null | undefined {
  if (value == null || !Number.isFinite(value)) return value;
  const decimal = value >= 1 ? value / 100 : value;
  return Math.min(Math.max(decimal, 0), 0.99);
}

function normalizeDecimalRate(
  value: number | null | undefined,
): number | null | undefined {
  if (value == null || !Number.isFinite(value)) return value;
  return Math.min(Math.max(value, 0), 0.99);
}

function normalizeCategoryOverheadRates(
  rates: BudgetRates["categoryOverheadRates"] | null | undefined,
): BudgetRates["categoryOverheadRates"] | null | undefined {
  if (!rates || typeof rates !== "object") return rates;
  const normalized: NonNullable<BudgetRates["categoryOverheadRates"]> = {};
  const materials = normalizePercentLikeRate(rates.materials);
  const labor = normalizePercentLikeRate(rates.labor);
  const equipment = normalizePercentLikeRate(rates.equipment);
  const subcontractors = normalizePercentLikeRate(rates.subcontractors);
  if (materials != null) normalized.materials = materials;
  if (labor != null) normalized.labor = labor;
  if (equipment != null) normalized.equipment = equipment;
  if (subcontractors != null) normalized.subcontractors = subcontractors;
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize override payloads into engine-decimal conventions.
 *
 * UI/API callers may send method-specific rates as whole percentages
 * (e.g. 30 for 30%). The pricing engine always expects decimals.
 */
export function normalizeEstimateRateOverrides(
  overrides: EstimateRateOverrides,
): EstimateRateOverrides {
  return {
    ...overrides,
    overheadRate: normalizeDecimalRate(overrides.overheadRate),
    profitMargin: normalizeDecimalRate(overrides.profitMargin),
    contingencyRate: normalizeDecimalRate(overrides.contingencyRate),
    categoryOverheadRates: normalizeCategoryOverheadRates(
      overrides.categoryOverheadRates,
    ),
    materialMarkupRate: normalizePercentLikeRate(overrides.materialMarkupRate),
    subcontractorMarkupRate: normalizePercentLikeRate(
      overrides.subcontractorMarkupRate,
    ),
    equipmentMarkupRate: normalizePercentLikeRate(
      overrides.equipmentMarkupRate,
    ),
  };
}

/**
 * Normalize a BudgetRates object into engine conventions (decimal rates).
 * Primarily protects against legacy snapshots that stored method-specific
 * rates as whole percentages.
 */
export function normalizeBudgetRatesForEngine(rates: BudgetRates): BudgetRates {
  return {
    ...rates,
    overheadRate: normalizePercentLikeRate(rates.overheadRate) ?? undefined,
    profitMargin: normalizePercentLikeRate(rates.profitMargin) ?? undefined,
    marginRate: normalizePercentLikeRate(rates.marginRate) ?? undefined,
    contingencyRate:
      normalizePercentLikeRate(rates.contingencyRate) ?? undefined,
    categoryOverheadRates:
      normalizeCategoryOverheadRates(rates.categoryOverheadRates) ?? undefined,
    materialMarkupRate:
      normalizePercentLikeRate(rates.materialMarkupRate) ?? undefined,
    subcontractorMarkupRate:
      normalizePercentLikeRate(rates.subcontractorMarkupRate) ?? undefined,
    equipmentMarkupRate:
      normalizePercentLikeRate(rates.equipmentMarkupRate) ?? undefined,
  };
}

/**
 * Apply per-estimate overrides from calculation_breakdown.overrides onto BudgetRates.
 * Only non-null override values replace the base rates.
 */
function applyEstimateOverrides(
  rates: BudgetRates,
  breakdown: Record<string, unknown>,
): void {
  const rawOverrides = breakdown.overrides as EstimateRateOverrides | undefined;
  if (!rawOverrides || typeof rawOverrides !== "object") return;
  const overrides = normalizeEstimateRateOverrides(rawOverrides);

  if (typeof overrides.overheadRate === "number") {
    rates.overheadRate = overrides.overheadRate;
  }
  if (typeof overrides.profitMargin === "number") {
    rates.profitMargin = overrides.profitMargin;
    rates.marginRate = overrides.profitMargin;
  }
  if (typeof overrides.contingencyRate === "number") {
    rates.contingencyRate = overrides.contingencyRate;
  }
  if (typeof overrides.overheadRecoveryMethod === "string") {
    rates.overheadRecoveryMethod = overrides.overheadRecoveryMethod;
  }
  if (typeof overrides.includeContingency === "boolean") {
    rates.includeContingency = overrides.includeContingency;
  }
  if (typeof overrides.overheadFixedAmount === "number") {
    rates.overheadFixedAmount = overrides.overheadFixedAmount;
  }
  if (typeof overrides.overheadPerLaborHour === "number") {
    rates.overheadPerLaborHour = overrides.overheadPerLaborHour;
  }
  if (
    overrides.categoryOverheadRates &&
    typeof overrides.categoryOverheadRates === "object"
  ) {
    rates.categoryOverheadRates = {
      ...rates.categoryOverheadRates,
      ...overrides.categoryOverheadRates,
    };
  }
  if (typeof overrides.materialMarkupRate === "number") {
    rates.materialMarkupRate = overrides.materialMarkupRate;
  }
  if (typeof overrides.subcontractorMarkupRate === "number") {
    rates.subcontractorMarkupRate = overrides.subcontractorMarkupRate;
  }
  if (typeof overrides.equipmentMarkupRate === "number") {
    rates.equipmentMarkupRate = overrides.equipmentMarkupRate;
  }
  if (
    overrides.contingencyBasis === "direct" ||
    overrides.contingencyBasis === "subtotal"
  ) {
    rates.contingencyBasis = overrides.contingencyBasis;
  }
}

/**
 * Merge overrides into an existing calculation_breakdown snapshot.
 * Returns the updated breakdown ready to write back to the DB.
 */
export function mergeOverridesIntoSnapshot(
  existingBreakdown: Record<string, unknown> | null,
  overrides: EstimateRateOverrides,
): Record<string, unknown> {
  const breakdown = existingBreakdown ?? {};
  const normalizedOverrides = normalizeEstimateRateOverrides(overrides);

  // Clean null values (null = clear override)
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(normalizedOverrides)) {
    if (value !== null && value !== undefined) {
      cleaned[key] = value;
    }
  }

  return {
    ...breakdown,
    overrides: Object.keys(cleaned).length > 0 ? cleaned : undefined,
  };
}

/**
 * Build BudgetRates from an estimate's rich calculation_breakdown snapshot.
 *
 * Phase H.3: the legacy flat-column fallback (margin_percentage,
 * overhead_percentage, contingency_percentage) was removed now that every
 * estimate carries a rich snapshot (Phase D backfill + migration drop).
 * Returns null when the breakdown is missing or has no snapshot_version so
 * callers can fall through to the resolver's other layers.
 */
export function estimateSnapshotToBudgetRates(estimate: {
  calculation_breakdown?: Record<string, unknown> | null;
}): BudgetRates | null {
  const breakdown = estimate.calculation_breakdown;
  if (
    !breakdown ||
    typeof breakdown !== "object" ||
    !breakdown.snapshot_version
  ) {
    return null;
  }

  const rates: BudgetRates = normalizeBudgetRatesForEngine({
    overheadRate: (breakdown.overheadRate as number) ?? 0,
    marginRate: (breakdown.marginRate as number) ?? 0,
    profitMargin: (breakdown.profitMargin as number) ?? undefined,
    contingencyRate: (breakdown.contingencyRate as number) ?? undefined,
    overheadRecoveryMethod:
      breakdown.overheadRecoveryMethod as BudgetRates["overheadRecoveryMethod"],
    categoryOverheadRates:
      breakdown.categoryOverheadRates as BudgetRates["categoryOverheadRates"],
    overheadFixedAmount: (breakdown.overheadFixedAmount as number) ?? undefined,
    overheadCap: (breakdown.overheadCap as number) ?? undefined,
    overheadFloor: (breakdown.overheadFloor as number) ?? undefined,
    derivedLaborHourlyRate:
      (breakdown.derivedLaborHourlyRate as number) ?? undefined,
    salesTaxRate: (breakdown.salesTaxRate as number) ?? undefined,
    laborBurdenRate: (breakdown.laborBurdenRate as number) ?? undefined,
    contingencyBasis:
      breakdown.contingencyBasis as BudgetRates["contingencyBasis"],
    includeContingency:
      typeof breakdown.includeContingency === "boolean"
        ? breakdown.includeContingency
        : undefined,
    costDistribution:
      breakdown.costDistribution as BudgetRates["costDistribution"],
    // Phase G.4: actuals intentionally not read here. They live at
    // calculation_breakdown.actuals and are computed by
    // compute-estimate-actuals.ts.
  });

  // Apply per-estimate overrides if present
  applyEstimateOverrides(rates, breakdown);

  return rates;
}

/**
 * Serialize BudgetRates into a JSONB-safe object for calculation_breakdown.
 * Only includes non-undefined fields to keep storage lean.
 */
export function budgetRatesToSnapshot(
  rates: BudgetRates,
): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {
    snapshot_version: 1,
    overheadRate: rates.overheadRate,
    marginRate: rates.marginRate,
  };
  if (rates.profitMargin !== undefined)
    snapshot.profitMargin = rates.profitMargin;
  if (rates.contingencyRate !== undefined)
    snapshot.contingencyRate = rates.contingencyRate;
  if (rates.overheadRecoveryMethod)
    snapshot.overheadRecoveryMethod = rates.overheadRecoveryMethod;
  if (rates.categoryOverheadRates)
    snapshot.categoryOverheadRates = rates.categoryOverheadRates;
  if (rates.overheadFixedAmount !== undefined)
    snapshot.overheadFixedAmount = rates.overheadFixedAmount;
  if (rates.overheadCap !== undefined) snapshot.overheadCap = rates.overheadCap;
  if (rates.overheadFloor !== undefined)
    snapshot.overheadFloor = rates.overheadFloor;
  if (rates.derivedLaborHourlyRate != null)
    snapshot.derivedLaborHourlyRate = rates.derivedLaborHourlyRate;
  if (rates.salesTaxRate !== undefined)
    snapshot.salesTaxRate = rates.salesTaxRate;
  if (rates.laborBurdenRate != null)
    snapshot.laborBurdenRate = rates.laborBurdenRate;
  if (rates.contingencyBasis)
    snapshot.contingencyBasis = rates.contingencyBasis;
  if (rates.includeContingency !== undefined)
    snapshot.includeContingency = rates.includeContingency;
  if (rates.costDistribution)
    snapshot.costDistribution = rates.costDistribution;
  // Phase G.4: actuals deliberately NOT written here. Actuals are a
  // separate concern owned by compute-estimate-actuals.ts, which writes
  // to calculation_breakdown.actuals directly. The old round-trip left
  // the rate pipeline vulnerable to accidentally clobbering actuals data.
  return snapshot;
}

/**
 * Attach budget-derived labor hourly rate to BudgetRates when
 * assumptions contain allocation fields (Professional+ bottom-up budgets).
 */
function attachDerivedLaborRate(
  rates: BudgetRates,
  assumptions: Record<string, unknown> | null | undefined,
): void {
  if (!assumptions) return;
  const derived = deriveBudgetRates(assumptions as BudgetAssumptionsInput);
  if (derived.laborHourlyRate !== null) {
    rates.derivedLaborHourlyRate = derived.laborHourlyRate;
  }
}

/**
 * Fetch organization settings and convert to BudgetRates.
 * Returns null if org not found or settings are empty.
 */
async function fetchOrgSettingsAsBudgetRates(
  supabase: SupabaseClient<Database>,
  organizationDbId: string,
): Promise<BudgetRates | null> {
  try {
    const { data, error } = await supabase
      .from("organizations")
      .select("settings")
      .eq("organization_id", organizationDbId)
      .single();

    if (error || !data?.settings) {
      return null;
    }

    const settings = data.settings as OrganizationSettings;
    const rates = orgSettingsToBudgetRates(settings);

    logger.info("Using organization settings as budget rate fallback", {
      organizationDbId,
      overheadRate: rates.overheadRate,
      marginRate: rates.marginRate,
      profitMargin: rates.profitMargin,
    });

    return rates;
  } catch {
    return null;
  }
}

/**
 * Fetch budget rates for an estimate.
 *
 * Rate resolution order:
 * 1. Budget assumptions (if estimate has a budget_id)
 * 2. Division budget (if estimate is in a division with an active budget)
 * 3. Organization settings (always available fallback)
 */
export async function fetchBudgetRatesForEstimate(
  supabase: SupabaseClient<Database>,
  estimateBudgetId: string | null | undefined,
  organizationDbId: string,
  divisionId?: string | null,
): Promise<BudgetRates | null> {
  // No budget assigned — try division budget, then fall back to org settings.
  //
  // Rule alignment: create-estimate.ts picks division budgets by
  // `is_default=true` (see validateAndResolveBudget). This function previously
  // used `period_start desc` as a heuristic which disagreed with the create
  // path whenever the is_default budget wasn't the most recently started one.
  // Now we prefer `is_default=true` and fall back to `period_start desc` only
  // as a tiebreaker when no budget in the division is marked default.
  if (!estimateBudgetId) {
    if (divisionId) {
      // First: look for an is_default=true budget in this division
      let divBudget: { assumptions: unknown } | null = null;
      {
        const { data } = await supabase
          .from("budgets")
          .select("assumptions")
          .eq("organization_id", organizationDbId)
          .eq("division_id", divisionId)
          .eq("is_default", true)
          .in("status", ["active", "published"])
          .limit(1)
          .maybeSingle();
        divBudget = data;
      }

      // Tiebreaker: most recent period_start if no default is flagged
      if (!divBudget) {
        const { data } = await supabase
          .from("budgets")
          .select("assumptions")
          .eq("organization_id", organizationDbId)
          .eq("division_id", divisionId)
          .in("status", ["active", "published"])
          .order("period_start", { ascending: false })
          .limit(1)
          .maybeSingle();
        divBudget = data;
      }

      if (divBudget?.assumptions) {
        const assumptions =
          typeof divBudget.assumptions === "object" &&
          divBudget.assumptions !== null
            ? (divBudget.assumptions as Record<string, unknown>)
            : null;
        const rates = extractBudgetRates(assumptions);
        if (rates) {
          attachDerivedLaborRate(rates, assumptions);
          logger.info("Using division budget rates", {
            divisionId,
            organizationDbId,
          });
          return rates;
        }
      }
    }
    return fetchOrgSettingsAsBudgetRates(supabase, organizationDbId);
  }

  try {
    // Fetch budget and budget lines in parallel
    const [budgetResult, budgetLinesResult] = await Promise.all([
      supabase
        .from("budgets")
        .select("assumptions")
        .eq("budget_id", estimateBudgetId)
        .eq("organization_id", organizationDbId)
        .single(),
      // Fetch level-1 lines only — level-0 are parent group totals that
      // duplicate the children. Summing all levels double-counts everything.
      supabase
        .from("budget_lines")
        .select("account_type, amount, level, dimension_type")
        .eq("budget_id", estimateBudgetId)
        .eq("organization_id", organizationDbId)
        .eq("level", 1),
    ]);

    const { data: budget, error } = budgetResult;
    const { data: budgetLines, error: linesError } = budgetLinesResult;

    if (error || !budget?.assumptions) {
      return fetchOrgSettingsAsBudgetRates(supabase, organizationDbId);
    }

    const assumptions =
      typeof budget.assumptions === "object" && budget.assumptions !== null
        ? (budget.assumptions as Record<string, unknown>)
        : null;

    // Determine if this is a QB-imported budget (assumptions have stale ratios
    // derived from previous-year data — actual budget lines are more reliable)
    const isQBImported =
      typeof assumptions?.source === "string" &&
      (assumptions.source as string).toLowerCase().includes("quickbooks");

    // Compute rates from budget lines first — these are the real numbers
    const linesRates = computeRatesFromBudgetLines(
      budgetLines,
      linesError,
      estimateBudgetId,
    );

    // Extract rates from budget assumptions (structured rate fields)
    let rates = extractBudgetRates(assumptions);

    // Budget-line-derived grossMargin is company-level (revenue - COGS / revenue).
    // Do NOT use it as profitMargin — that conflates gross margin with per-item profit.
    // profitMargin should come from net_profit_margin in assumptions or org settings.
    // Cost distribution from lines IS useful — it tells us the COGS category breakdown.
    if (!rates) {
      const orgFallback = await fetchOrgSettingsAsBudgetRates(
        supabase,
        organizationDbId,
      );
      rates = orgFallback ?? {
        overheadRate: BUSINESS_RULES.DEFAULT_OVERHEAD_RATE,
        marginRate: BUSINESS_RULES.DEFAULT_MARGIN_RATE,
      };
    }
    if (linesRates?.costDistribution) {
      rates.costDistribution = linesRates.costDistribution;
    }

    // Attach budget-derived labor rate from allocation fields
    if (rates) {
      attachDerivedLaborRate(rates, assumptions);
    }

    // Log extracted rates for debugging
    if (rates) {
      logger.info("Extracted budget rates", {
        budgetId: estimateBudgetId,
        source: isQBImported ? "budget_lines (QB)" : "assumptions",
        overheadRate: rates.overheadRate,
        marginRate: rates.marginRate,
        profitMargin: rates.profitMargin,
        overheadRecoveryMethod: rates.overheadRecoveryMethod,
        derivedLaborHourlyRate: rates.derivedLaborHourlyRate,
        laborBurdenRate: rates.laborBurdenRate,
      });
    } else {
      logger.warn("Failed to extract budget rates", {
        budgetId: estimateBudgetId,
        hasAssumptions: !!assumptions,
        assumptionsKeys: assumptions ? Object.keys(assumptions) : [],
        hasLines: !linesError && (budgetLines?.length ?? 0) > 0,
      });
    }

    return rates;
  } catch {
    // Budget fetch failed — fall back to org settings
    return fetchOrgSettingsAsBudgetRates(supabase, organizationDbId);
  }
}

/**
 * Compute gross margin and cost distribution from level-1 budget lines.
 * Level-0 lines are parent group totals that duplicate child amounts —
 * summing all levels double-counts everything.
 */
function computeRatesFromBudgetLines(
  budgetLines: Array<{
    account_type: string | null;
    amount: number | null;
    level: number | null;
    dimension_type: string | null;
  }> | null,
  linesError: unknown,
  budgetId: string,
): {
  grossMargin: number | undefined;
  totalIncome: number;
  totalCOGS: number;
  costDistribution?: BudgetRates["costDistribution"];
} | null {
  if (linesError || !budgetLines || budgetLines.length === 0) return null;

  const totalIncome = budgetLines
    .filter((line) => line.account_type === "income")
    .reduce((sum, line) => sum + (line.amount ?? 0), 0);

  const cogsLines = budgetLines.filter(
    (line) => line.account_type === "cost_of_goods_sold",
  );
  const totalCOGS = cogsLines.reduce(
    (sum, line) => sum + (line.amount ?? 0),
    0,
  );

  // Gross margin: (Revenue - COGS) / Revenue
  let grossMargin: number | undefined;
  if (totalIncome > 0) {
    const raw =
      Math.round(((totalIncome - totalCOGS) / totalIncome) * 10000) / 10000;
    // Only use if positive and reasonable (0-100%)
    if (raw >= 0 && raw < 1) {
      grossMargin = raw;
    }
  }

  // Cost distribution from COGS dimension_type breakdown
  let costDistribution: BudgetRates["costDistribution"] = undefined;
  if (totalCOGS > 0) {
    const byDimension = (dim: string): number | undefined => {
      const amt = cogsLines
        .filter((l) => l.dimension_type === dim)
        .reduce((sum, l) => sum + (l.amount ?? 0), 0);
      return amt > 0
        ? Math.round((amt / totalCOGS) * 10000) / 10000
        : undefined;
    };
    const materials = byDimension("materials");
    const labor = byDimension("labor");
    const equipment = byDimension("equipment");
    const subcontractors = byDimension("subcontractors");
    if (materials || labor || equipment || subcontractors) {
      costDistribution = { materials, labor, equipment, subcontractors };
    }
  }

  if (grossMargin === undefined && !costDistribution) return null;

  logger.info("Computed rates from budget lines (level-1 only)", {
    budgetId,
    totalIncome,
    totalCOGS,
    grossMargin,
    lineCount: budgetLines.length,
  });

  return { grossMargin, totalIncome, totalCOGS, costDistribution };
}
